import { useState } from 'react'

import { Box, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { LedgerAccountsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DeleteLedgerAccountDocument,
  EnableLedgerAccountDocument,
  LedgerAccountsPageDocument,
  LedgerAccountsPageQuery,
  LegalEntityLedgerAccountPageFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LedgerAccountsDataTable from 'modules/finance/components/Accounting/Accounts/DataTable'
import DisableLedgerAccountModal from 'modules/finance/components/Accounting/Accounts/DisableLedgerAccountModal'
import EditLedgerAccountModal from 'modules/finance/components/Accounting/Accounts/EditLedgerAccountModal'
import { AddNewAccountsLedgerAccountCTA } from 'modules/finance/components/Accounting/AddNewAccountsLedgerAccountCTA'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

export type LedgerAccountsLegalEntity =
  LedgerAccountsPageQuery['companies']['legalEntities'][number]

const AccountsPage = () => {
  const [showExportSidepanel, setShowExportSidepanel] = useState<boolean>(false)
  const [showCTAaddNewAccounts, setShowCTAaddNewAccounts] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showDisableModal, setShowDisableModal] = useState<boolean>(false)
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)

  const [legalEntity, setLegalEntity] = useState<LegalEntityLedgerAccountPageFragment>()
  const { employee } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()
  const [deleteLedgerAccount] = useMutation(DeleteLedgerAccountDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceAccount,
    refetchQueries: [LedgerAccountsDataTableDocument],
  })
  const [enableLedgerAccount] = useMutation(EnableLedgerAccountDocument, {
    extractorFn: (data) => data.financeMutations.enableFinanceAccount,
    refetchQueries: [LedgerAccountsDataTableDocument],
  })

  const { error, data, loading } = useQuery(LedgerAccountsPageDocument)

  const canSeeAccounts = data?.finance?._policies?.accountingSeeAccounts?.can ?? false
  const canManageAccounts = data?.finance?._policies?.accountingManageAccounts?.can ?? false
  const canManageJournalEntries = data?.finance?._policies?.journalEntriesManage?.can ?? false
  const legalEntities = useUnsafeMemo(() => data?.companies?.legalEntities ?? [], [data])

  const legalEntitiesOptions =
    legalEntities.map((le) => ({ label: le.legalName ?? '', value: le.id })) ?? []

  const getDefaultLegalEntityId = useUnsafeMemo(() => {
    if (data && data.companies.legalEntities.length > 0) {
      let preferedLegalEntity = data.companies.legalEntities.find(
        (el) => el.id === employee?.legalEntity?.id
      )
      if (!preferedLegalEntity) {
        preferedLegalEntity = data.companies.legalEntities.find((el) => el.main)
      }
      return preferedLegalEntity?.id
    }
  }, [data, employee?.legalEntity?.id])

  useUnsafeEffect(() => {
    if (getDefaultLegalEntityId) {
      setLegalEntity(legalEntities.find((le) => le.id === getDefaultLegalEntityId))
    }
  }, [getDefaultLegalEntityId, legalEntities, loading])

  useUnsafeEffect(() => {
    if (!loading && data && canSeeAccounts) {
      const accountsCount = data.finance.accountsConnection?.totalCount || 0
      track(trackingEvents.accounting.accounting.sectionChartOfAccounts, {
        has_data: accountsCount > 0,
      })
    }
  }, [data, loading, canSeeAccounts])

  const onLegalEntityChange = useUnsafeCallback(
    (value: Record<string, unknown>) => {
      setLegalEntity(legalEntities.find((le) => le.id === value.legalEntityId))
    },
    [legalEntities]
  )

  const showEditModalForAccountId = (id: number) => {
    setSelectedAccountId(id)
    setShowEditModal(true)
  }

  const showDeleteModalForAccountId = (id: number, name?: string | null) => {
    openDialog({
      title: i18n.t('finance.chart_of_accounts.delete.title_v2', { name: name || '' }),
      content: i18n.t('finance.chart_of_accounts.delete.description_v3'),
      illustration: 'delete',
      confirmLabel: i18n.t('finance.buttons.delete'),
      onConfirm: async () => {
        const result = await deleteLedgerAccount({
          variables: {
            id,
          },
        })

        const handled = handleResult(result, (errors) => {
          app.setFlash({
            type: 'wrong',
            message: errors.errors || i18n.t('errors.generic_error'),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('finance.chart_of_accounts.delete.success'),
          })
        }
      },
    })
  }

  const showEnableDisableModalForAccountId = (
    id: number,
    disabled: boolean,
    name?: string | null
  ) => {
    if (disabled) {
      return openDialog({
        title: i18n.t('finance.chart_of_accounts.enable.title_v3', { name: name || '' }),
        content: i18n.t('finance.chart_of_accounts.enable.description_v3'),
        illustration: 'check',
        confirmLabel: i18n.t('finance.chart_of_accounts.enable.activate'),
        onConfirm: async () => {
          const result = await enableLedgerAccount({
            variables: {
              id,
            },
          })

          const handled = handleResult(result, (errors) => {
            app.setFlash({
              type: 'wrong',
              message: errors.errors || i18n.t('errors.generic_error'),
            })
          })

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('finance.chart_of_accounts.enable.success_v2'),
            })
          }
        },
      })
    }

    setSelectedAccountId(id)
    setShowDisableModal(true)
  }

  return (
    <ResponsivePage.Body authorized={canSeeAccounts} error={error} loading={loading}>
      <Box paddingTop="s32">
        <LedgerAccountsDataTable
          onFilterChange={onLegalEntityChange}
          legalEntitiesOptions={legalEntitiesOptions}
          defaultLegalEntityId={getDefaultLegalEntityId}
          showEditAccount={showEditModalForAccountId}
          showEnableDisableAccount={showEnableDisableModalForAccountId}
          showDeleteAccount={showDeleteModalForAccountId}
          canManageJournalEntries={canManageJournalEntries}
          canManageAccounts={canManageAccounts}
          onExportClick={() => setShowExportSidepanel(true)}
          addAccountCTA={
            canManageAccounts
              ? () => {
                  track(trackingEvents.accounting.accounting.addNewAccounts)
                  setShowCTAaddNewAccounts(true)
                }
              : undefined
          }
        />
      </Box>

      {showExportSidepanel && (
        <ExportSidepanel resource="finance.account" onClose={() => setShowExportSidepanel(false)} />
      )}
      {legalEntity && showCTAaddNewAccounts && (
        <AddNewAccountsLedgerAccountCTA
          legalEntities={legalEntities ?? []}
          selectedLegalEntityId={legalEntity.id}
          onClose={() => setShowCTAaddNewAccounts(false)}
        />
      )}
      {showEditModal && selectedAccountId && (
        <EditLedgerAccountModal id={selectedAccountId} onClose={() => setShowEditModal(false)} />
      )}
      {showDisableModal && selectedAccountId && (
        <DisableLedgerAccountModal
          id={selectedAccountId}
          onClose={() => {
            setShowDisableModal(false)
          }}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default AccountsPage
